
import React, { useEffect, useState, useRef } from 'react';
import { useAIAgent } from '../../context/AIAgentContext';
import { useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, List, Info, ZapIcon, Bot, Send, MessageSquare } from 'lucide-react';
import RootCauseAnalysis from './RootCauseAnalysis';
import ScenarioExplorer from './ScenarioExplorer';

const AIAgentPanel = () => {
  const { isAgentActive, analysisLevel, drillDownAnalysis, currentScenario, setCurrentScenario } = useAIAgent();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('');
  const [insights, setInsights] = useState([]);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const path = location.pathname;
    const pageName = path.split('/').filter(segment => segment !== '')[0] || 'home';
    setCurrentPage(pageName);
    
    generatePageInsights(pageName);
  }, [location.pathname]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generatePageInsights = (page) => {
    const pageInsightsMap = {
      'competitor-360': [
        'Competitor market share has declined 3.2% in the last quarter',
        'Your pricing is on average 2.5% higher than nearest competitors',
        'Customer retention is 15% better than closest competitor',
        'Opportunity to expand in north region where competitor presence is weak'
      ],
      'trade-area-analyzer': [
        'High traffic periods show 23% increase in premium fuel purchases',
        'Customers from 5-10km radius spend 17% more on convenience items',
        'Morning rush has different buying patterns than evening rush',
        'Weather correlation shows 12% sales increase during rainy days'
      ],
      'executive-dashboard': [
        'Top 3 stations account for 35% of total revenue',
        'Margin variation between urban and rural stations is significant',
        'Weekend promotions show 8.5% better conversion than weekday',
        'Q3 growth is outpacing market average by 3.1%'
      ],
      'segmentation-explorer': [
        'Premium customer segment has grown 4.7% YoY',
        'Value-conscious segment responds best to loyalty programs',
        'Business accounts show consistent 3-month purchase cycles',
        'Cross-selling opportunity identified in fleet segment'
      ],
      'campaign-management': [
        'Email campaigns have 2.3x ROI compared to SMS',
        'Loyalty customer conversion is 27% higher than non-loyalty',
        'Time-based promotions perform best between 3-6pm',
        'Geographic targeting improved campaign efficiency by 18%'
      ],
      'home': [
        'System-wide analysis shows opportunities in fleet segment',
        'Competitive intelligence suggests pricing adjustment in urban zones',
        'Customer segmentation reveals new behavioral patterns',
        'Campaign performance indicates mobile promotion effectiveness'
      ]
    };
    
    setInsights(pageInsightsMap[page] || pageInsightsMap['home']);
  };

  const handleBackButton = () => {
    if (chatMode) {
      setChatMode(false);
      return;
    }
    
    if (currentScenario) {
      setCurrentScenario(null);
      return;
    }
    
    drillDownAnalysis(Math.max(0, analysisLevel - 1));
  };

  const handleSendMessage = () => {
    if (!userInput.trim()) return;
    
    const userMessage = {
      sender: 'user',
      text: userInput
    };
    
    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    
    // Simulate AI response (in a real app, this would call an API)
    setTimeout(() => {
      const aiResponse = generateAIResponse(userInput);
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: aiResponse
      }]);
    }, 500);
  };

  const generateAIResponse = (query) => {
    const responses = {
      'performance': 'Based on current data, your performance metrics show a 12% increase compared to last quarter. The main drivers are increased customer retention and higher average transaction value.',
      'competitor': 'Your nearest competitor has recently launched a new loyalty program that appears to be gaining traction. Their market share has increased by 1.5% in the southern region.',
      'recommendation': 'I recommend focusing on the premium customer segment in urban areas. Data shows they have 28% higher lifetime value and respond well to personalized offers.',
      'forecast': 'Our forecasting models predict a 7-9% growth in the coming quarter, with highest potential in the suburban areas and premium fuel categories.',
      'default': 'I analyze your market data to find actionable insights. Based on your current context, I suggest focusing on customer segmentation and targeted promotions. Would you like more specific recommendations?'
    };
    
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('performance') || lowerQuery.includes('metrics') || lowerQuery.includes('kpi')) {
      return responses.performance;
    } else if (lowerQuery.includes('competitor') || lowerQuery.includes('competition')) {
      return responses.competitor;
    } else if (lowerQuery.includes('recommend') || lowerQuery.includes('suggest') || lowerQuery.includes('what should')) {
      return responses.recommendation;
    } else if (lowerQuery.includes('forecast') || lowerQuery.includes('predict') || lowerQuery.includes('future')) {
      return responses.forecast;
    } else {
      return responses.default;
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const activateChatMode = () => {
    setChatMode(true);
    setMessages([
      {
        sender: 'ai',
        text: 'Hello! I\'m your Fuel.AI assistant. How can I help you with your business analytics today?'
      }
    ]);
  };

  if (!isAgentActive) return null;

  return (
    <div className="fixed left-20 bottom-4 w-96 h-[500px] bg-white rounded-lg shadow-xl border border-gray-200 z-40 flex flex-col overflow-hidden transition-all">
      <div className="bg-purple-700 text-white p-4">
        <div className="flex items-center gap-2">
          {(analysisLevel > 0 || currentScenario || chatMode) && (
            <button 
              onClick={handleBackButton}
              className="text-white hover:text-purple-200 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h3 className="font-medium text-lg flex items-center gap-2">
            <Bot size={18} /> Fuel AI Agent
          </h3>
        </div>
        <p className="text-sm text-purple-100">
          {chatMode 
            ? 'Chat with your AI assistant' 
            : `Contextual insights for ${currentPage.replace('-', ' ')}`}
        </p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {chatMode ? (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  message.sender === 'user' 
                    ? 'bg-purple-100 text-purple-900' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className="text-sm">{message.text}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <>
            {analysisLevel === 0 && !currentScenario && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <ZapIcon size={16} /> Key Insights
                  </h4>
                  <button 
                    onClick={activateChatMode}
                    className="text-sm text-purple-700 flex items-center hover:text-purple-900"
                  >
                    <MessageSquare size={14} className="mr-1" />
                    Chat Mode
                  </button>
                </div>
                <ul className="space-y-3">
                  {insights.map((insight, index) => (
                    <li key={index} className="bg-purple-50 p-3 rounded-md flex items-start">
                      <Info size={16} className="text-purple-600 mt-1 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-800">{insight}</p>
                        <button 
                          onClick={() => drillDownAnalysis(1)}
                          className="mt-2 text-xs text-purple-700 flex items-center hover:underline"
                        >
                          Analyze root causes <ArrowRight size={12} className="ml-1" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {analysisLevel > 0 && !currentScenario && (
              <RootCauseAnalysis 
                level={analysisLevel} 
                onBack={() => drillDownAnalysis(Math.max(0, analysisLevel - 1))}
                onDrillDown={() => drillDownAnalysis(Math.min(4, analysisLevel + 1))}
                currentPage={currentPage}
              />
            )}
            
            {currentScenario && (
              <ScenarioExplorer 
                scenario={currentScenario}
                onBack={() => setCurrentScenario(null)}
                currentPage={currentPage}
              />
            )}
          </>
        )}
      </div>
      
      {chatMode ? (
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center">
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleInputKeyPress}
              placeholder="Ask me anything..."
              className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={handleSendMessage}
              className="bg-purple-600 text-white rounded-r-md px-3 py-2 text-sm hover:bg-purple-700 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center text-xs text-gray-500">
            <List size={14} className="mr-1" />
            {analysisLevel > 0 ? 
              `Analysis Level: ${analysisLevel}/4` : 
              'View insights and analysis'}
          </div>
          <button 
            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 px-3 py-1 rounded"
            onClick={() => window.open("#", "_blank")}
          >
            Learn more
          </button>
        </div>
      )}
    </div>
  );
};

export default AIAgentPanel;
